import React, { useRef, useState, useEffect, useCallback } from 'react';

// Hooks
import { useCruiseDataApi } from './hooks/useCruiseDataApi';
import useCruiseData from './hooks/useCruiseData';

import ProgressIndicator from './components/ProgressIndicator';

// Pages
import BookingWidget from './components/BookingWidget';
import ShippingFlowLegs from './pages/legs';
import ShippingFlowAddress from './pages/address/ShippingFlowAddress';
import ShippingFlowPayment from './pages/payment/ShippingFlowPayment';

import { ThemeProvider } from './context/ThemeContext';

import { useScrollTo } from './hooks/useScrollTo'
import { useFormStore } from './stores/formData';
import SequenceTooltip from './components/SequenceTooltip';
import useRailsAuth from './hooks/useRailsAuth';
import { usePickupFees } from './hooks/usePickupFees';
import {
  CRUISE_FLOW_HISTORY_KEY,
  historyOpForNext,
  historyOpForPrev,
  historyOpForSelect,
  makeLandingState,
  makeStep1State,
  pageIndexFromPopState
} from './utils/cruiseFlowHistory';

export { CRUISE_FLOW_HISTORY_KEY } from './utils/cruiseFlowHistory';

const sequenceSteps = [
  {
    id: 'origin-address',
    content: 'Enter the address where your shipment will be picked up, or click pickup from a cruise.',
    position: 'top'
  },
  {
    id: 'destination-address', 
    content: 'Enter the address where your shipment will be delivered, or click deliver to a cruise.',
    position: 'top'
  },
];

// PropTypes (now imported from external module)
import {
  ShippingFlowState,
  ShippingFlowDefaultState
} from './constants/types';

const ShippingFlow = ({ partnerConfig, onStartBooking, onExitBooking }) => {
  const [currentPage, setCurrentPage] = useState(-1);
  const [partnerId] = useState(window.gon?.partner_id || partnerConfig?.partnerId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getCruiseData } = useCruiseDataApi();
  const [activeCruisePartners, setActiveCruisePartners] = useState([]);
  const { scrollToTop } = useScrollTo();
  
  const { checkExistingAuth } = useRailsAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [showSequence, setShowSequence] = useState(false);

  // only used what is needed to avoid rerenders
  const cruiseData = useFormStore((state) => state.formData.cruiseData);
  const customerInfo = useFormStore((state) => state.formData.customerInfo);
  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);

  const originRef = useRef(null);
  const destinationRef = useRef(null);

  const stepRefs = {
    'origin-address': originRef,
    'destination-address': destinationRef,
  };

  const { 
    cruises: activeCruisePartnerData, 
  } = useCruiseData();

  const {
    pickupFees,
    groupedFees,
    defaultPickupFee,
    isLoading:pickupFeesLoading,
    refetch
  } = usePickupFees(window.gon?.current_brand, {
    enabled: true
  });

  const handleSequenceComplete = () => {
  };

  const handleSequenceSkip = () => {
  };

  const handleSequenceReset = () => {
    sessionStorage.removeItem('sequence-booking-tutorial-completed');
  };

  useEffect(() => {
    const safeReplaceState = (state) => {
      try {
        window.history.replaceState(state, document.title, window.location.href);
      } catch (_err) {
      }
    };

    const initialState = window.history.state;
    const hasOurState = initialState?.flowKey === CRUISE_FLOW_HISTORY_KEY;
    if (!hasOurState) {
      safeReplaceState(makeLandingState(initialState || {}));
    }

    const handlePopState = (event) => {
      const nextPageIndex = pageIndexFromPopState(event.state);
      if (nextPageIndex === null) return;

      if (nextPageIndex === -1) {
        setCurrentPage(-1);
        onExitBooking?.();
        return;
      }

      setCurrentPage(nextPageIndex);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onExitBooking]);

  useEffect(() => {
    // Check for existing auth on mount
    if (customerInfo?.authToken?.length) return;

    const existingAuth = checkExistingAuth();
    if (existingAuth?.authToken) {
      // save this data into state
      const customerInfo = {
        id: existingAuth.user.id,
        authToken: existingAuth.authToken,
        firstName: existingAuth.user.firstName || existingAuth.user.full_name?.split(' ')[0] || '',
        lastName: existingAuth.user.lastName || existingAuth.user.full_name?.split(' ')[1] || '',
        email: existingAuth.user.email || '',
        phoneNumber: existingAuth.user.phone_number || '',
        role: existingAuth.user.role || ''
      }
      actions.form.updateFields({ 
        authToken: existingAuth.authToken,
        customerInfo: customerInfo
      });
    }
  }, [customerInfo]);

  useEffect(() => {
    if (groupedFees?.hourly?.length > 0) {
      const standardFees = groupedFees?.standard?.filter(fee => fee.price_cents > 0);
      actions.form.updateField('hourlyPickupFees', 
        [...standardFees, ...groupedFees.hourly.filter(fee => fee.price_cents > 0)]
      );
    }
  }, [groupedFees]);

  useEffect(() => {
    // download active cruise lines from the host
    if (
      activeCruisePartnerData?.length > 0) {
      actions.cruise.setData(activeCruisePartnerData);
      setActiveCruisePartners(activeCruisePartnerData);
    } 
  }, [activeCruisePartnerData]);

  //Fetch cruise data on component mount
  useEffect(() => {
    if(!cruiseData?.length)
      return;

    const hasActiveCruise = cruiseData.some(c => c?.cruise_sessions?.length > 0);
    if( hasActiveCruise)
      return;

    const actives = activeCruisePartners.reduce((hash, obj) => {
      hash[obj.id] = obj;
      return hash;
    }, {});

    const fetchCruiseData = async () => {
      const currentPartnerId = partnerId || window.gon?.partner_id || null;
      
      if (!loading) {
        setLoading(true);
        setError(null);
        
        const results = await getCruiseData({
          partnerId: partnerConfig.path !== 'cruises' ? currentPartnerId : null
        });
        
        if (results.success && results.data) {
          const data = results.data;
          // filter the active cruise lines
          let filteredData = data.filter(cruise => {
            const active = actives[cruise.id];
            return active && active.partner_id === cruise.partner_id 
          });
          
          if(partnerConfig.path !== 'cruises') {
            const selectedCruise = data.find(c => c.partner_id === window.gon?.partner_id);
            filteredData = [ selectedCruise ];
          }
          if(filteredData?.length) actions.cruise.setData(filteredData);

          const options = filteredData.map((cruise) => ({
            'id': cruise.club_id,
            'icon_url': cruise.icon_url,
            'formatted_address': cruise.name
          }));
          // If we have a partner id, lock in the cruise partner name
          if (options.length === 1 && currentPartnerId) {
            const cruiseLine = data?.[0];
            actions.cruise.setCurrentLine(cruiseLine);

            const firstSession = cruiseLine?.cruise_sessions?.[0];
            if (firstSession?.tour_name) {
              actions.cruise.updateItineraryFilter('tour_name', 'Tour Name');
            }
            
            localStorage.setItem('currentCruiseLine', JSON.stringify(cruiseLine));
            localStorage.setItem('cruisePremiumPartner-' + partnerId, window.location.href);
          }
        } else {
          setError(results.message || 'Failed to load cruise data');
        }
        setLoading(false);
      }
    };

    fetchCruiseData();
  }, [cruiseData, partnerId]);

  const handleStartBooking = useCallback(({ from, to, pricing }) => {
    // update leg 0 with the booking widget data
    const legs = helpers.getLegs() || [];
    const newLegs = legs.map((leg, index) => {
        return index === 0 ? {
          ...leg, 
        fromAddress: from,
        toAddress: to,
        pricing: pricing,
        tripType: {
          id: 'one-way',
          label: 'One Way'
        }
      } : leg
    });
    actions.form.updateField('legs', newLegs);

    // Create a history entry so browser Back returns to landing page.
    try {
      window.history.pushState(
        makeStep1State(),
        document.title,
        window.location.href
      );
    } catch (_err) {
    }

    setCurrentPage(0);
    onStartBooking?.();
  }, [actions, onStartBooking]);

  const nextPage = useCallback(() => {
    const transition = historyOpForNext(currentPage);
    if (!transition) return;

    try {
      if (transition.op === 'push') {
        window.history.pushState(transition.state, document.title, window.location.href);
      }
      if (transition.op === 'replace') {
        window.history.replaceState(transition.state, document.title, window.location.href);
      }
    } catch (_err) {
    }

    setCurrentPage(transition.nextPage);
    scrollToTop();
  }, [currentPage, scrollToTop]);

  const prevPage = useCallback(() => {
    const transition = historyOpForPrev(currentPage);
    if (!transition) return;

    if (transition.op === 'back') {
      try {
        window.history.back();
      } catch (_err) {
        setCurrentPage(transition.nextPage);
      }
      return;
    }

    try {
      window.history.replaceState(transition.state, document.title, window.location.href);
    } catch (_err) {
    }

    setCurrentPage(transition.nextPage);
  }, [currentPage]);

  
  const pages = [
    { component: ShippingFlowLegs, title: 'Itinerary' },
    { component: null, title: 'Traveler Details' },
    { component: null, title: 'Payment' },
  ];

  if (currentPage === -1) {
    return (
      <BookingWidget  partnerConfig={partnerConfig} onBook={handleStartBooking} />
    );
  }

  return (
    <ThemeProvider defaultTheme="light">
      <div className="bg-white p-2 border rounded-md">
        <ProgressIndicator 
          currentPage={currentPage}
          pages={pages} 
          onSelectCurrentPage={(page) => {
            const transition = historyOpForSelect({ currentPage, targetPage: page });
            if (!transition) return;

            if (transition.op === 'back') {
              try {
                window.history.back();
              } catch (_err) {
                setCurrentPage(transition.nextPage);
              }
              return;
            }

            if (transition.op === 'push' || transition.op === 'replace') {
              try {
                if (transition.op === 'push') {
                  window.history.pushState(transition.state, document.title, window.location.href);
                } else {
                  window.history.replaceState(transition.state, document.title, window.location.href);
                }
              } catch (_err) {
              }
            }

            setCurrentPage(transition.nextPage);
          }}
          className="w-[50%]"
        />

        
        <div className="w-full min-h-[850px] overflow-y-auto overflow-x-hidden">
          {currentPage == 0 && (
            <div className="w-full">
              <ShippingFlowLegs 
                onClickContinue={nextPage}
                canShowBack={() => false}
                onClickBack={prevPage}
                originRef={originRef}
                destinationRef={destinationRef}
              />
            </div>
          )}

          {currentPage == 1 && (
            <div className="w-full">
              <ShippingFlowAddress 
                onClickContinue={nextPage}
                canShowBack={() => true}
                onClickBack={prevPage}
              />
            </div>
          )}

          {currentPage == 2 && (
            <div className="w-full">
              <ShippingFlowPayment 
                canShowBack={() => true}
                onClickBack={prevPage}
              />
            </div>
          )}
        </div>

        {showSequence && 
          <SequenceTooltip
            id="booking-flow-tutorial"
            steps={sequenceSteps}
            refs={stepRefs}
            startDelay={1000}
            stepDelay={500}
            enabled={false}
            onComplete={handleSequenceComplete}
            onSkip={handleSequenceSkip}
            onReset={handleSequenceReset}
          />
        }
      </div>
    </ThemeProvider>
  );
};

// Apply PropTypes and defaultProps from external module
ShippingFlow.propTypes = ShippingFlowState;
ShippingFlow.defaultProps = ShippingFlowDefaultState

export default ShippingFlow;
